import { useEffect } from 'react';
import { ImageType } from '@/components/image-generator/FilterBar/TypeFilter';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { saveImageFilters, getImageFilters } from '@/utils/localStorage';

// Interface for the hook props
interface UseFilterPersistenceProps {
  selectedType: ImageType | null;
  selectedStyle: string;
  setSelectedStyle: (style: string) => void;
  styleOptions: FilterOption[];
  setStyleOptions: (options: FilterOption[]) => void;
  selectedInfluence: string;
  setSelectedInfluence: (influence: string) => void;
  influenceOptions: FilterOption[];
  setInfluenceOptions: (options: FilterOption[]) => void;
  selectedBackground: string;
  setSelectedBackground: (background: string) => void;
  backgroundOptions: FilterOption[];
  setBackgroundOptions: (options: FilterOption[]) => void;
  colorOptions: FilterOption[];
  setColorOptions: (options: FilterOption[]) => void;
  environmentOptions: FilterOption[];
  setEnvironmentOptions: (options: FilterOption[]) => void;
  moodOptions: FilterOption[];
  setMoodOptions: (options: FilterOption[]) => void;
  imageDescription: string;
  setImageDescription: (description: string) => void;
  width: string;
  height: string;
  setWidth: (width: string) => void;
  setHeight: (height: string) => void;
  setSelectedType: (type: ImageType | null) => void;
  isAdvancedMode: boolean;
  setIsAdvancedMode: (value: boolean) => void;
}

/**
 * Hook to handle saving and restoring filter state from localStorage
 */
export const useFilterPersistence = ({
  selectedType,
  selectedStyle,
  setSelectedStyle,
  styleOptions,
  setStyleOptions,
  selectedInfluence,
  setSelectedInfluence,
  influenceOptions,
  setInfluenceOptions,
  selectedBackground,
  setSelectedBackground,
  backgroundOptions,
  setBackgroundOptions,
  colorOptions,
  setColorOptions,
  environmentOptions,
  setEnvironmentOptions,
  moodOptions,
  setMoodOptions,
  imageDescription,
  setImageDescription,
  width,
  height,
  setWidth,
  setHeight,
  setSelectedType,
  isAdvancedMode,
  setIsAdvancedMode
}: UseFilterPersistenceProps) => {
  
  // Save filter settings to localStorage when they change
  useEffect(() => {
    // Get active IDs for multi-select filters
    const activeColorIds = colorOptions.filter(o => o.checked).map(o => o.id);
    const activeEnvironmentIds = environmentOptions.filter(o => o.checked).map(o => o.id);
    const activeMoodIds = moodOptions.filter(o => o.checked).map(o => o.id);
    
    // Save filter settings
    saveImageFilters({
      selectedType,
      selectedStyle,
      selectedInfluence,
      selectedBackground,
      width,
      height,
      activeColorIds,
      activeEnvironmentIds,
      activeMoodIds,
      imageDescription,
      isAdvancedMode
    });
  }, [
    selectedType,
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    colorOptions,
    environmentOptions,
    moodOptions,
    width,
    height,
    imageDescription,
    isAdvancedMode
  ]);
  
  // Load filter settings from localStorage on first render
  useEffect(() => {
    const savedFilters = getImageFilters();
    if (!savedFilters) return;
    
    // Set selected type
    if (savedFilters.selectedType) {
      setSelectedType(savedFilters.selectedType as ImageType);
    }
    
    // Set selected style
    if (savedFilters.selectedStyle) {
      setSelectedStyle(savedFilters.selectedStyle);
    }
    
    // Set selected influence
    if (savedFilters.selectedInfluence) {
      setSelectedInfluence(savedFilters.selectedInfluence);
    }
    
    // Set selected background
    if (savedFilters.selectedBackground) {
      setSelectedBackground(savedFilters.selectedBackground);
    }
    
    // Set dimensions
    if (savedFilters.width) {
      setWidth(savedFilters.width);
    }
    
    if (savedFilters.height) {
      setHeight(savedFilters.height);
    }
    
    // Set image description
    if (savedFilters.imageDescription) {
      setImageDescription(savedFilters.imageDescription);
    }
    
    // Set advanced mode (default to false if not saved)
    if (savedFilters.isAdvancedMode !== undefined) {
      setIsAdvancedMode(savedFilters.isAdvancedMode);
    }
    
    // Update color options with saved selections
    if (savedFilters.activeColorIds && savedFilters.activeColorIds.length > 0) {
      const updatedColorOptions = colorOptions.map(option => ({
        ...option,
        checked: savedFilters.activeColorIds.includes(option.id)
      }));
      setColorOptions(updatedColorOptions);
    }
    
    // Update environment options with saved selections
    if (savedFilters.activeEnvironmentIds && savedFilters.activeEnvironmentIds.length > 0) {
      const updatedEnvironmentOptions = environmentOptions.map(option => ({
        ...option,
        checked: savedFilters.activeEnvironmentIds.includes(option.id)
      }));
      setEnvironmentOptions(updatedEnvironmentOptions);
    }
    
    // Update mood options with saved selections
    if (savedFilters.activeMoodIds && savedFilters.activeMoodIds.length > 0) {
      const updatedMoodOptions = moodOptions.map(option => ({
        ...option,
        checked: savedFilters.activeMoodIds.includes(option.id)
      }));
      setMoodOptions(updatedMoodOptions);
    }
  }, [
    // Only run once on component mount, so no dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);
};
